import React, { useEffect } from 'react';
import {
    Button,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Theme,
    makeStyles,
    Grid,
    Box,
} from '@material-ui/core';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { ICreateVehicleForm, TCreateVehicleFormikValues } from './types';
import { Edit, Save } from 'react-feather';
import useMake from 'src/hooks/useMake';
import useVehicle from 'src/hooks/useVehicle';
import { IMake } from 'src/interfaces/make';
import useAuth from 'src/hooks/useAuth';
import { CapitalizeNames } from 'src/utils/capitalize';
import { IVehicle } from 'src/interfaces/vehicle';
import { useServiceClient } from 'src/hooks';
import { Alert } from '@material-ui/lab';
import moment from 'moment';
import { getVehicleYearsForSelect } from 'src/constants/vehicles';

const defaultValues: TCreateVehicleFormikValues = {
    make: '',
    model: '',
    modelType: '',
    kilometers: 0,
    vin: '',
    year: '',
};

const useStyles = makeStyles((theme: Theme) => ({
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    addDetailButton: {
        maxWidth: 250,
        selfAlign: 'center',
    },
    createButton: {
        minWidth: 150,
        height: 56,
    },
}));

export const ServiceClientVehicleForm: React.FC<ICreateVehicleForm> = ({
    mode,
    initialValues = defaultValues,
    onSubmit,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { error } = useServiceClient();
    const { makes, getMakes } = useMake();
    const {
        vehicles,
        getVehiclesAdvancedResults,
        clearState: clearVehicles,
    } = useVehicle();
    const { user } = useAuth();

    useEffect(() => {
        if (user._id) getMakes();
    }, [user]);

    useEffect(() => {
        if (mode === 'edit' && initialValues?.make) {
            getVehiclesAdvancedResults({
                filters: {
                    make: initialValues.make,
                },
                select: 'make model modelType',
                populate: 'make',
                sort: 'make model',
            });
        }
    }, [mode, initialValues?.make]);

    const CURRENT_YEAR = Number(moment().format('yyyy'));
    const yearOptions = getVehicleYearsForSelect();

    const validationSchema = Yup.object({
        make: Yup.string().required(t('Yup.MakeReq')),
        model: Yup.string().required(t('Yup.VehicleReq')),
        kilometers: Yup.number()
            .typeError(t('Yup.Number'))
            .required(t('Yup.Required')),
        vin: Yup.string().required(t('Yup.Required')),
        year: Yup.number()
            .required(t('Yup.YearReq'))
            .max(CURRENT_YEAR + 1, t('Yup.Year')),
    });

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
        >
            {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                setFieldValue,
            }) => (
                <Form>
                    <Card elevation={0}>
                        <CardHeader
                            title={
                                mode === 'create'
                                    ? t('serviceClient.CreateVehicle')
                                    : t('serviceClient.UpdateVehicle')
                            }
                        />
                        <Divider />
                        <CardContent className={classes.formContainer}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <FormControl
                                        fullWidth
                                        error={
                                            touched.make && Boolean(errors.make)
                                        }
                                    >
                                        <InputLabel style={{ marginLeft: 12 }}>
                                            {t('serviceClient.Make')}
                                        </InputLabel>
                                        <Select
                                            variant="outlined"
                                            name="make"
                                            value={values.make}
                                            onChange={(event) => {
                                                handleChange(event);
                                                if (event.target.value) {
                                                    getVehiclesAdvancedResults({
                                                        filters: {
                                                            make: event.target
                                                                .value,
                                                        },
                                                        select: 'make model modelType',
                                                        populate: 'make',
                                                        sort: 'make model',
                                                    });
                                                    setFieldValue('model', '');
                                                    setFieldValue(
                                                        'modelType',
                                                        ''
                                                    );
                                                } else {
                                                    clearVehicles();
                                                    setFieldValue('model', '');
                                                    setFieldValue(
                                                        'modelType',
                                                        ''
                                                    );
                                                }
                                            }}
                                            onBlur={handleBlur}
                                        >
                                            {makes.map((make: IMake) => (
                                                <MenuItem
                                                    key={make._id}
                                                    value={make._id}
                                                >
                                                    {CapitalizeNames(make.name)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            {touched.make && errors.make}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl
                                        fullWidth
                                        error={
                                            touched.model &&
                                            Boolean(errors.model)
                                        }
                                    >
                                        <InputLabel style={{ marginLeft: 12 }}>
                                            {t('serviceClient.Model')}
                                        </InputLabel>
                                        <Select
                                            disabled={!values.make}
                                            variant="outlined"
                                            name="model"
                                            value={values.model}
                                            onChange={(event) => {
                                                handleChange(event);
                                                const selectedVehicle =
                                                    vehicles.find(
                                                        (vehicle: IVehicle) =>
                                                            vehicle._id ===
                                                            event.target.value
                                                    );
                                                setFieldValue(
                                                    'modelType',
                                                    selectedVehicle?.modelType ||
                                                        ''
                                                );
                                            }}
                                            onBlur={handleBlur}
                                        >
                                            {vehicles.map(
                                                (vehicle: IVehicle) => (
                                                    <MenuItem
                                                        key={vehicle._id}
                                                        value={vehicle._id}
                                                    >
                                                        {CapitalizeNames(
                                                            vehicle.make?.name +
                                                                ' ' +
                                                                vehicle.model
                                                        )}
                                                    </MenuItem>
                                                )
                                            )}
                                        </Select>
                                        <FormHelperText>
                                            {touched.model && errors.model}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        disabled
                                        label={t('serviceClient.ModelType')}
                                        name="modelType"
                                        value={values.modelType}
                                        onBlur={handleBlur}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl
                                        fullWidth
                                        error={
                                            touched.year && Boolean(errors.year)
                                        }
                                    >
                                        <InputLabel style={{ marginLeft: 12 }}>
                                            {t('serviceClient.Year')}
                                        </InputLabel>
                                        <Select
                                            variant="outlined"
                                            name="year"
                                            value={values.year}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        >
                                            {yearOptions.map((year) => (
                                                <MenuItem
                                                    key={year}
                                                    value={year}
                                                >
                                                    {year}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            {touched.year && errors.year}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        label={t('serviceClient.Kilometers')}
                                        name="kilometers"
                                        type="number"
                                        value={values.kilometers}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                            touched.kilometers &&
                                            Boolean(errors.kilometers)
                                        }
                                        helperText={
                                            touched.kilometers &&
                                            errors.kilometers
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        label="VIN"
                                        name="vin"
                                        value={values.vin}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                            touched.vin && Boolean(errors.vin)
                                        }
                                        helperText={touched.vin && errors.vin}
                                    />
                                </Grid>
                            </Grid>

                            {error && <Alert severity="error">{error}</Alert>}

                            <Box display="flex" justifyContent="flex-end">
                                <Button
                                    type="submit"
                                    color="primary"
                                    variant="contained"
                                    startIcon={
                                        mode === 'create' ? <Save /> : <Edit />
                                    }
                                    className={classes.createButton}
                                >
                                    {mode === 'create'
                                        ? t('Buttons.Create')
                                        : t('Buttons.Update')}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Form>
            )}
        </Formik>
    );
};
